import React from 'react';

import {
  Create,
  SimpleForm,
  TextInput,
  ReferenceArrayInput,
  SelectArrayInput,
  SelectInput,
  usePermissions,
  required,
  FormDataConsumer,
} from 'react-admin';

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
} from '@mui/material';
import cleaner from '../../utilities/dataProvider/cleaner';
import { ProductTypes, productTypesChoices } from '../../constants/productTypes';
import { ProductPartiesInputFields } from '../../customFields/ProductParties';
import ProductInformationInput from './productInformation/inputs';
import actions, { Actions } from '../../constants/actions';
import hasAccess from '../../utilities/hasAccess';
import { EntityTypes } from '../../constants/entityTypes';
import { ProductPartyRoles } from '../../constants/productPartyRoles';

const productbaseValidation = (
  _value: string,
  allValues: { customId: string; productNumber: string},
) => {
  const { customId, productNumber } = allValues;

  if (!customId && !productNumber) return 'One of Custom id or Product number must be defined';
  return undefined;
};

type FormData = {
  customId?: string;
  productNumber?: string;
  productDescription?: string;
  productType?: ProductTypes;
  groups?: string[];
  productInformation?: unknown;
  parties?: {
    type: EntityTypes;
    id: string;
    role: ProductPartyRoles;
  }[];
}

const ProductCreate = () => {
  const { permissions } = usePermissions<Actions[]>();

  return (
    <Create transform={cleaner}>
      <Card variant="outlined">
        <CardContent>
          <SimpleForm mode="all" component={Box}>
            <Grid container spacing={6}>
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <TextInput fullWidth source="customId" label="Custom id" validate={productbaseValidation} />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <TextInput source="productNumber" fullWidth validate={productbaseValidation} />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <TextInput source="productDescription" fullWidth />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <SelectInput source="productType" choices={productTypesChoices} validate={required()} fullWidth />
              </Grid>
              {hasAccess(permissions, actions.GROUP_LIST) && (
                <Grid
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <ReferenceArrayInput perPage={100} label="Groups" source="groups" reference="groups" filter={{ active: true }}>
                    <SelectArrayInput optionText="name" fullWidth />
                  </ReferenceArrayInput>
                </Grid>
              )}
              <Grid size={12}>
                <Card variant="outlined" sx={{ m: 0 }}>
                  <CardHeader
                    sx={{
                      color: 'black',
                      fontWeight: 'bold',
                      flexWrap: 'wrap',
                    }}
                    slotProps={{ title: { variant: 'inherit' } }}
                    title="Product information"
                  />
                  <Divider />
                  <CardContent>
                    <ProductInformationInput />
                  </CardContent>
                </Card>
              </Grid>
              {(hasAccess(permissions, actions.ACCOUNT_ENTITY_LIST)
                || hasAccess(permissions, actions.INDIVIDUAL_LIST)
                || hasAccess(permissions, actions.BUSINESS_LIST))
                && (
                  <FormDataConsumer<FormData>>
                    {({ formData }) => (formData.productType && (
                      <Grid size={12}>
                        <Card variant="outlined" sx={{ m: 0 }}>
                          <CardHeader
                            sx={{
                              color: 'black',
                              fontWeight: 'bold',
                              flexWrap: 'wrap',
                            }}
                            slotProps={{ title: { variant: 'inherit' } }}
                            title="Parties"
                          />
                          <Divider />
                          <CardContent>
                            <ProductPartiesInputFields productType={formData.productType} />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </FormDataConsumer>
                )}
            </Grid>
          </SimpleForm>
        </CardContent>
      </Card>
    </Create>
  );
};

export default ProductCreate;
